/* eslint-disable react/no-unescaped-entities */
import { Title, DocumentList, DocumentSegment, Render } from "@clubofcode/ui";
import clsx from "clsx";
import { type TdHTMLAttributes } from "react";
import { LegalLayout } from "~/components/Layout";

type CellProps = {
  children: React.ReactNode;
  className?: string;
  bold?: boolean;
} & React.DetailedHTMLProps<
  TdHTMLAttributes<HTMLTableCellElement>,
  HTMLTableCellElement
>;

const Cell = ({ children, className, bold, ...tdProps }: CellProps) => (
  <td
    className={clsx(
      "py-2 px-1",
      {
        "font-medium": bold,
      },
      className,
    )}
    {...tdProps}
  >
    {children}
  </td>
);

type MeasuresTableProps = {
  title: string;
  description: string;
  // The measures we take.
  measures?: string[];
  additionalMeasures?: string[] | string;
};

/**
 * A table to display all organisational and technical measures
 */
const MeasuresTable = (props: MeasuresTableProps) => {
  return (
    <table className="table-auto text-left">
      <tbody className="align-text-top">
        <tr>
          <Cell colSpan={2} className="bg-gray-100 border-b border-b-gray-300">
            <Title as="h6" className="mb-2" bold>
              {props.title}
            </Title>
            <p>{props.description}</p>
          </Cell>
        </tr>
        <Render if={Array.isArray(props.measures) && props.measures.length}>
          <tr>
            <Cell>
              <p>Maßnahmen:</p>
              <ul>
                {props.measures?.map((measure, i) => (
                  <li key={i}>- {measure}</li>
                ))}
              </ul>
            </Cell>
          </tr>
        </Render>
        <tr>
          <Cell>
            <p>Weitere umgesetzte Maßnahmen / Erläuterungen:</p>
            <Render
              if={
                props.additionalMeasures?.length &&
                Array.isArray(props.additionalMeasures)
              }
              else={
                <p>
                  Diese oder weitere Maßnahmen werden teilweise oder vollständig
                  durch unsere Dienstleister umgesetzt. Bei Interesse an den
                  konkreten technischen und organisatorischen Maßnahmen der
                  Dienstleister, sprechen Sie uns gerne an.
                </p>
              }
            >
              <ul>
                {Array.isArray(props.additionalMeasures) &&
                  props.additionalMeasures?.map((measure, i) => (
                    <li key={i}>{measure}</li>
                  ))}
              </ul>
            </Render>
          </Cell>
        </tr>
      </tbody>
    </table>
  );
};

const DataProcessingAgreementPage = () => {
  return (
    <LegalLayout>
      <section className="text-justify space-y-10">
        <div className="text-center mb-12">
          <Title as="h1" className="text-xl font-bold">
            Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO
          </Title>
        </div>
        <p>
          Der vorliegende Auftragsverarbeitungsvertrag („<b>AVV</b>“) gilt für
          die Verarbeitungsmaßnahmen personenbezogener Daten durch die Club of
          Code Technology UG & Co. KG, Heringer Fahrweg 3, 65597 Hünfelden,
          Deutschland (auch als „<b>wir</b>" oder „<b>Auftragnehmer</b>"
          bezeichnet), die gegenüber Kunden (nachfolgend „<b>Auftraggeber</b>“
          oder „<b>Sie</b>“) in Erfüllung des Hauptvertrages erbracht werden.
        </p>
        <DocumentSegment title="Präambel">
          <p>
            Der Auftragnehmer erbringt für den Auftraggeber Leistungen gemäß dem
            zwischen ihnen geschlossenen Lizenzvertrag zur Inanspruchnahme der
            “Club of Code”-Software zur Abbildung von Recruiting-Prozessen von
            Software-Entwicklern und ähnlichen Berufen in Festanstellung oder
            einer freiberuflichen Tätigkeit (im Folgenden: „<b>Hauptvertrag</b>
            “). Teil der Durchführung des Hauptvertrags ist die Verarbeitung von
            personenbezogenen Daten im Sinne der Datenschutzgrundverordnung („
            <b>DSGVO</b>“). Zur Erfüllung der Anforderungen der DSGVO an
            derartige Konstellationen schließen die Parteien den nachfolgenden
            Auftragsverarbeitungsvertrag (auch „<b>Vertrag</b>“), der mit
            Unterzeichnung bzw. Wirksamwerden des Hauptvertrages zustande kommt.
          </p>
        </DocumentSegment>
        <DocumentSegment title="§ 1 Gegenstand/Umfang der Beauftragung">
          <DocumentList>
            <li>
              Im Rahmen der Zusammenarbeit der Parteien nach Maßgabe des
              Hauptvertrages hat der Auftragnehmer Zugriff auf personenbezogene
              Daten des Auftraggebers (nachfolgend „<b>Auftraggeberdaten</b>“).
              Diese Auftraggeberdaten verarbeitet der Auftragnehmer im Auftrag
              und nach Weisung des Auftraggebers im Sinne von Art. 4 Nr. 8 und
              Art. 28 DSGVO.
            </li>
            <li>
              Die Verarbeitung der Auftraggeberdaten durch den Auftragnehmer
              erfolgt in der in den Anlagen beschriebenen Art sowie in dem dort
              spezifizierten Umfang und Zweck. Der Kreis der von der
              Datenverarbeitung betroffenen Personen wird dargestellt. Die Dauer
              der Verarbeitung entspricht der Laufzeit des Hauptvertrages.
            </li>
            <li>
              Ob die Leistungen des Auftragnehmers für die Verarbeitung
              besonderer Kategorien personenbezogener Daten gemäß Art. 9 Abs. 1
              DSGVO geeignet sind, bedarf einer Risikobewertung durch den
              Auftraggeber.
            </li>
            <li>
              Dem Auftragnehmer ist eine von den in den Anlagen genannten
              Verarbeitungen abweichende Verarbeitung von Auftraggeberdaten
              untersagt.
            </li>
            <li>
              Die Verarbeitung der Auftraggeberdaten findet grds. im Gebiet der
              Bundesrepublik Deutschland, in einem Mitgliedsstaat der
              Europäischen Union oder in einem anderen Vertragsstaat des
              Abkommens über den Europäischen Wirtschaftsraum statt. Sollte es
              eine Verlagerung der Auftragsverarbeitung in ein Drittland geben,
              bedarf dies der vorherigen Zustimmung des Auftraggebers und
              erfolgt nur, wenn die besonderen Voraussetzungen der Art. 44 bis
              49 DSGVO erfüllt sind. Der Auftraggeber stimmt bereits mit
              Abschluss dieses Auftragsverarbeitungsvertrages der Verarbeitung
              personenbezogener Daten durch die in den Anlagen genannten
              Subunternehmen zu.
            </li>
            <li>
              Die Bestimmungen dieses Vertrages finden Anwendung auf alle
              Tätigkeiten, die mit dem Hauptvertrag in Zusammenhang stehen.
              Gleiches gilt für alle Tätigkeiten, bei denen der Auftragnehmer
              und seine Beschäftigten oder durch den Auftragnehmer Beauftragte
              mit Auftraggeberdaten in Berührung kommen.
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 2 Weisungsbefugnisse des Auftraggebers">
          <DocumentList>
            <li>
              Der Auftragnehmer verarbeitet die Auftraggeberdaten im Rahmen der
              Beauftragung und im Auftrag und nach Weisung des Auftraggebers i.
              S. v. Art. 28 DSGVO (Auftragsverarbeitung). Der Auftraggeber hat
              das alleinige Recht, Weisungen über Art, Umfang, und Methode der
              Verarbeitungstätigkeiten zu erteilen (nachfolgend auch „
              <b>Weisungsrecht</b>“). Wird der Auftragnehmer durch das Recht der
              Europäischen Union oder der Mitgliedstaaten, dem er unterliegt, zu
              weiteren Verarbeitungen verpflichtet, teilt er dem Auftraggeber
              diese rechtlichen Anforderungen vor der Verarbeitung mit.
            </li>
            <li>
              Weisungen werden vom Auftraggeber grundsätzlich schriftlich oder
              in elektronischer Form (E-Mail ausreichend) erteilt; mündlich
              erteilte Weisungen sind vom Auftragnehmer in elektronischer Form
              zu bestätigen.
            </li>
            <li>
              Ist der Auftragnehmer der Ansicht, dass eine Weisung des
              Auftraggebers gegen datenschutzrechtliche Bestimmungen verstößt,
              hat er den Auftraggeber darauf hinzuweisen. Der Auftragnehmer ist
              berechtigt, die Durchführung der betreffenden Weisung solange
              auszusetzen, bis diese durch den Auftraggeber bestätigt oder
              geändert wird.
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 3 Schutzmaßnahmen des Auftragnehmers">
          <DocumentList>
            <li>
              Der Auftragnehmer ist verpflichtet, die gesetzlichen Bestimmungen
              über den Datenschutz zu beachten und die aus dem Bereich des
              Auftraggebers erlangten Informationen nicht an Dritte
              weiterzugeben oder deren Zugriff auszusetzen. Unterlagen und Daten
              sind gegen die Kenntnisnahme durch Unbefugte unter
              Berücksichtigung des Stands der Technik zu sichern.
            </li>
            <li>
              Ferner wird der Auftragnehmer alle Personen, die von ihm mit der
              Bearbeitung und der Erfüllung dieses Vertrages betraut werden (im
              Folgenden „<b>Mitarbeiter</b>“ genannt), zur Vertraulichkeit
              verpflichten (Verpflichtung zur Vertraulichkeit, Art. 28 Abs. 3
              lit. b DSGVO). Auf Verlangen des Auftraggebers wird der
              Auftragnehmer dem Auftraggeber die Verpflichtung der Mitarbeiter
              schriftlich oder in elektronischer Form nachweisen.
            </li>
            <li>
              Der Auftragnehmer wird seine innerbetriebliche Organisation so
              gestalten, dass sie den besonderen Anforderungen des Datenschutzes
              gerecht wird. Er verpflichtet sich, alle geeigneten technischen
              und organisatorischen Maßnahmen zum angemessenen Schutz der
              Auftraggeberdaten gem. Art. 32 DSGVO, insbesondere die in Anlage 2
              zu diesem Vertrag aufgeführten Maßnahmen, zu ergreifen und diese
              für die Dauer der Verarbeitung der Auftraggeberdaten
              aufrechtzuerhalten.
            </li>
            <li>
              Eine Änderung der getroffenen technischen und organisatorischen
              Maßnahmen bleibt dem Auftragnehmer vorbehalten, wobei er
              sicherstellt, dass das vertraglich vereinbarte Schutzniveau nicht
              unterschritten wird.{" "}
            </li>
            <li>
              Auf Verlangen des Auftraggebers wird der Auftragnehmer dem
              Auftraggeber die Einhaltung der technischen und organisatorischen
              Maßnahmen nachweisen.
            </li>
            <li>
              Der Auftragnehmer und die bei oder für ihn Beschäftigten ist
              berechtigt, die gem. des Hauptvertrags zu erbringenden Leistungen
              und damit auch die Verarbeitung personenbezogenen Daten aus seiner
              Hauptverwaltung, seinen Betriebsstätten, Niederlassungen oder aus
              dem Home- und Mobile-Office heraus erbringen zu lassen, sofern
              sichergestellt ist, dass die Schutzmaßnahmen, die in diesem AVV
              definiert werden, hierbei eingehalten werden.{" "}
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 4 Informations- und Unterstützungspflichten des Auftragnehmers">
          <DocumentList>
            <li>
              Bei Störungen, Verdacht auf Datenschutzverletzungen oder
              Verletzungen vertraglicher Verpflichtungen des Auftragnehmers,
              Verdacht auf sicherheitsrelevante Vorfälle oder andere
              Unregelmäßigkeiten bei der Verarbeitung der Auftraggeberdaten
              durch den Auftragnehmer, bei ihm im Rahmen des Auftrags
              beschäftigten Personen oder durch Dritte, wird der Auftragnehmer
              den Auftraggeber unverzüglich, spätestens aber innerhalb von 48
              Stunden in Schriftform oder elektronischer Form informieren.
              Dasselbe gilt für Prüfungen des Auftragnehmers durch die
              Datenschutz-Aufsichtsbehörde. Diese Meldungen sollten jeweils
              zumindest die in Art. 33 Absatz 3 DSGVO genannten Angaben
              enthalten.
            </li>
            <li>
              Der Auftragnehmer wird den Auftraggeber im o. g. Falle bei der
              Erfüllung seiner diesbezüglichen Aufklärungs-, Abhilfe- und
              Informationsmaßnahmen im Rahmen des Zumutbaren unterstützen.{" "}
            </li>
            <li>
              Der Auftragnehmer verpflichtet sich, dem Auftraggeber auf dessen
              Anforderung innerhalb angemessener Frist alle Auskünfte und
              Nachweise zur Verfügung zu stellen, die zur Durchführung einer
              Kontrolle erforderlich sind.
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 5 Sonstige Verpflichtungen des Auftragnehmers">
          <DocumentList>
            <li>
              Der Auftragnehmer ist, sofern die Voraussetzungen des Art. 30
              DSGVO auf ihn zutreffen, verpflichtet, ein Verzeichnis zu allen
              Kategorien von im Auftrag des Auftraggebers durchgeführten
              Tätigkeiten der Verarbeitung gem. Art. 30 Absatz 2 DSGVO zu
              führen. Das Verzeichnis ist dem Auftraggeber auf Verlangen zur
              Verfügung zu stellen.
            </li>
            <li>
              Der Auftragnehmer ist verpflichtet, den Auftraggeber bei der
              Erstellung einer Datenschutz-Folgenabschätzung nach Art. 35 DSGVO
              und einer etwaigen vorherigen Konsultation der Aufsichtsbehörde
              nach Art. 36 DSGVO zu unterstützen.
            </li>
            <li>
              Der Auftragnehmer bestätigt, dass er – soweit eine gesetzliche
              Verpflichtung hierzu besteht – einen Datenschutzbeauftragten
              bestellt hat.
            </li>
            <li>
              Sollten die Auftraggeberdaten beim Auftragnehmer durch Pfändung
              oder Beschlagnahme, durch ein Insolvenz- oder Vergleichsverfahren
              oder durch sonstige Ereignisse oder Maßnahmen Dritter gefährdet
              werden, so hat der Auftragnehmer den Auftraggeber unverzüglich
              darüber zu informieren, sofern ihm dies nicht durch gerichtliche
              oder behördliche Anordnung untersagt ist. Der Auftragnehmer wird
              in diesem Zusammenhang alle zuständigen Stellen unverzüglich
              darüber informieren, dass die Entscheidungshoheit über die Daten
              ausschließlich beim Auftraggeber als „Verantwortlichem“ im Sinne
              der DSGVO liegt.
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 6 Subunternehmerverhältnisse">
          <DocumentList>
            <li>
              Der Auftragnehmer darf die Verarbeitung personenbezogener Daten
              ganz oder teilweise durch weitere Auftragsverarbeiter (nachfolgend
              „<b>Unterauftragnehmer</b>“) erbringen lassen. Der Auftragnehmer
              informiert den Auftraggeber in Textform rechtzeitig vorab über die
              Beauftragung von Unterauftragnehmern oder Änderungen in der
              Unterbeauftragung. Der Auftraggeber kann bei Vorliegen sachlicher
              Gründe der Unterbeauftragung innerhalb von vier Wochen nach
              Kenntnisnahme in Textform widersprechen.{" "}
            </li>
            <li>
              Ein Subunternehmerverhältnis im Sinne dieser Bestimmungen liegt
              nicht vor, wenn der Auftragnehmer Dritte mit Dienstleistungen
              beauftragt, die als reine Nebenleistungen anzusehen sind. Dazu
              gehören z.B. Post-, Transport- und Versandleistungen,
              Reinigungsleistungen, Bewachungsdienste,
              Telekommunikationsleistungen ohne konkreten Bezug zu Leistungen,
              die der Auftragnehmer für den Auftraggeber erbringt sowie sonstige
              Maßnahmen zur Sicherstellung der Vertraulichkeit, Verfügbarkeit,
              Integrität und Belastbarkeit der Hard- und Software von
              Datenverarbeitungsanlagen. Die Pflicht des Auftragnehmers, auch in
              diesen Fällen die Beachtung von Datenschutz und Datensicherheit
              sicherzustellen, bleibt unberührt.
            </li>
            <li>
              Der Auftragnehmer wird mit dem Unterauftragnehmer die in diesem
              AVV getroffenen Regelungen inhaltsgleich vereinbaren. Insbesondere
              müssen die mit dem Unterauftragnehmer zu vereinbarenden TOM ein
              gleichwertiges Schutzniveau aufweisen.{" "}
            </li>
            <li>
              Der Auftragnehmer hat mit den in Anlage 1 genannten Unternehmen
              Subunternehmerverhältnisse begründet, denen der Auftraggeber mit
              Abschluss dieses Auftragsverarbeitungsvertrages zustimmt. Die in
              der Anlage 1 genannten Unternehmen können durch den Auftragnehmer
              ergänzt oder verringert werden. Sollte der Auftragnehmer einen
              weiteren Subunternehmer hinzufügen, so teilt er dies dem
              Auftraggeber in Textform mindestens 4 Wochen vor Inanspruchnahme
              der Leistungen des entsprechenden Subunternehmers mit. Sollte der
              Auftraggeber nicht mit der Hinzufügung des weiteren Subunternehmen
              einverstanden sein, so hat er die Möglichkeit, innerhalb von 3
              Wochen nach der entsprechenden Mitteilung gegenüber dem
              Auftragnehmer zu widersprechen. Widerspricht der Auftraggeber der
              Hinzufügung des weiteren Subunternehmen, so hat der Auftragnehmer
              das Recht den Hauptvertrag inkl. sämtlicher Anlagen innerhalb von
              1 Woche zu kündigen, sollte keine alternative Lösung zur weiteren
              Zusammenarbeit gefunden werden und sollte die Hinzufügung des
              weiteren Subunternehmen für das Unternehmen des Auftragnehmers von
              besonderer Wichtigkeit sein.
            </li>
            <li>
              Mit den Unterauftragnehmern hat der Auftragnehmer den
              Anforderungen aus § 6 Abs. 3 entsprechende
              Auftragsverarbeitungsverträge geschlossen. Mit Wirksamwerden
              dieses AVV genehmigt der Auftraggeber die vorgenannten
              Unterauftragnehmer.
            </li>
            <li>
              Bestandteil der Auftragsverarbeitungsverträge mit den
              Unterauftragnehmern ist insbesondere auch, dass die
              Unterauftragnehmer sicherstellen, ihrerseits angemessene und
              geeignete technische und organisatorische Maßnahmen nach Art. 32
              DSGVO wegen der von ihnen im Auftrag durchgeführten Verarbeitungen
              personenbezogener Daten getroffen zu haben.{" "}
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 7 Kontrollrechte">
          <DocumentList>
            <li>
              Der Auftraggeber ist berechtigt, sich regelmäßig von der
              Einhaltung der Regelungen dieses Vertrages zu überzeugen. Hierfür
              kann er z. B. Auskünfte des Auftragnehmers einholen, sich
              vorhandene Testate von Sachverständigen, Zertifizierungen oder
              internen Prüfungen vorlegen lassen oder die technischen und
              organisatorischen Maßnahmen des Auftragnehmers zu den üblichen
              Geschäftszeiten selbst persönlich bzw. durch einen sachkundigen
              Dritten prüfen lassen, sofern dieser nicht in einem
              Wettbewerbsverhältnis zum Auftragnehmer steht.
            </li>
            <li>
              Der Auftraggeber wird Kontrollen nur im erforderlichen Umfang
              durchführen und angemessene Rücksicht auf die Betriebsabläufe des
              Auftragnehmers nehmen. Über den Zeitpunkt sowie die Art der
              Prüfung verständigen sich die Parteien rechtzeitig.
            </li>
            <li>
              Der Auftraggeber dokumentiert das Kontrollergebnis und teilt es
              dem Auftragnehmer mit. Bei Fehlern oder Unregelmäßigkeiten, die
              der Auftraggeber insbesondere bei der Prüfung von
              Auftragsergebnissen feststellt, hat er den Auftragnehmer
              unverzüglich zu informieren. Werden bei der Kontrolle Sachverhalte
              festgestellt, deren zukünftige Vermeidung Änderungen des
              angeordneten Verfahrensablaufs erfordern, teilt der Auftraggeber
              dem Auftragnehmer die notwendigen Verfahrensänderungen
              unverzüglich mit.
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 8 Rechte Betroffener">
          <DocumentList>
            <li>
              Der Auftragnehmer unterstützt den Auftraggeber nach Möglichkeit
              mit geeigneten technischen und organisatorischen Maßnahmen bei der
              Erfüllung von dessen Pflichten nach Art. 12 bis 22 sowie Art. 32
              bis 36 DSGVO. Er wird dem Auftraggeber unverzüglich, spätestens
              aber innerhalb von 14 Werktagen, die gewünschte Auskunft über
              Auftraggeberdaten geben, sofern der Auftraggeber nicht selbst über
              die entsprechenden Informationen verfügt.
            </li>
            <li>
              Macht der Betroffene seine Rechte gemäß Art. 16 bis 18 DSGVO
              geltend, ist der Auftragnehmer dazu verpflichtet, die
              Auftraggeberdaten auf Weisung des Auftraggebers unverzüglich,
              spätestens binnen einer Frist von 7 Werktagen zu berichtigen,
              löschen oder einzuschränken. Der Auftragnehmer wird dem
              Auftraggeber die Löschung, Berichtigung bzw. Einschränkung der
              Daten auf Verlangen schriftlich nachweisen.
            </li>
            <li>
              Macht ein Betroffener Rechte, etwa auf Auskunftserteilung,
              Berichtigung oder Löschung hinsichtlich seiner Daten, unmittelbar
              gegenüber dem Auftragnehmer geltend, wird der Auftragnehmer dieses
              Ersuchen an den Auftraggeber weiterleiten und wartet dessen
              Weisungen ab. Ohne entsprechende Einzelweisung wird der
              Auftragnehmer nicht mit der betroffenen Person in Kontakt treten.
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 9 Laufzeit und Kündigung">
          <p>
            Die Laufzeit dieses Vertrags entspricht der Laufzeit des
            Hauptvertrags. Er endet damit automatisch mit der Beendigung des
            Hauptvertrags. Ist der Hauptvertrag ordentlich kündbar, gelten die
            Regelungen zur ordentlichen Kündigung für diesen Vertrag
            entsprechend. Sollte der Auftragnehmer vor Ablauf des Hauptvertrages
            keine Auftraggeberdaten mehr verarbeiten, endet dieser Vertrag
            ebenfalls automatisch.
          </p>
        </DocumentSegment>
        <DocumentSegment title="§ 10 Löschung und Rückgabe nach Vertragsende">
          <DocumentList>
            <li>
              Der Auftragnehmer wird dem Auftraggeber nach Beendigung des
              Hauptvertrags oder jederzeit auf dessen Verlangen alle ihm
              überlassenen Unterlagen, Daten und Datenträger zurückgeben oder
              auf Wunsch des Auftraggebers, sofern nicht eine gesetzliche
              Aufbewahrungsfrist besteht, vollständig und unwiderruflich
              löschen. Dies gilt auch für Vervielfältigungen der
              Auftraggeberdaten beim Auftragnehmer, wie etwa Datensicherungen,
              nicht aber für Dokumentationen, die dem Nachweis der auftrags- und
              ordnungsgemäßen Verarbeitung der Auftraggeberdaten dienen. Solche
              Dokumentationen sind vom Auftragsnehmer für eine Dauer von 6
              Monaten aufzubewahren und auf Verlangen an den Auftragsgeber
              herauszugeben.
            </li>
            <li>
              Der Auftragnehmer wird dem Auftraggeber die Löschung elektronisch
              bestätigen. Der Auftraggeber hat das Recht, die vollständige und
              vertragsgerechte Rückgabe bzw. Löschung der Daten beim
              Auftragnehmer in geeigneter Weise zu kontrollieren.
            </li>
            <li>
              Der Auftragnehmer ist verpflichtet, auch über das Ende des
              Hauptvertrags hinaus die ihm im Zusammenhang mit dem Hauptvertrag
              bekannt gewordenen Daten vertraulich zu behandeln.
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 11 Haftung">
          <DocumentList>
            <li>
              Die Haftung der Parteien richtet sich nach Art. 82 DSGVO. Eine
              Haftung des Auftragnehmers gegenüber dem Auftraggeber wegen
              Verletzung von Pflichten aus diesem Vertrag oder dem Hauptvertrag
              bleibt hiervon unberührt.
            </li>
            <li>
              Die Parteien stellen sich jeweils von der Haftung frei, wenn eine
              Partei nachweist, dass sie in keinerlei Hinsicht für den Umstand,
              durch den der Schaden bei einem Betroffenen eingetreten ist,
              verantwortlich ist. Dies gilt im Falle einer gegen eine Partei
              verhängte Geldbuße entsprechend, wobei die Freistellung in dem
              Umfang erfolgt, in dem die jeweils andere Partei Anteil an der
              Verantwortung für den durch die Geldbuße sanktionierten Verstoß
              trägt.
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 12 Vertraulichkeit & Datengeheimnis">
          <DocumentList>
            <li>
              Der Auftragnehmer verpflichtet sich, die gleichen
              Geheimnisschutzregeln zu beachten, wie sie dem Auftraggeber
              obliegen.
            </li>
            <li>
              Es besteht eine Verschwiegenheitspflicht für die Mitarbeiter des
              Auftragnehmers und durch ihn beauftragte Dritte. Der Auftragnehmer
              hat die bei der Verarbeitung von Auftraggeberdaten beschäftigten
              Personen gemäß Art. 28 Abs. 3 lit. b DSGVO schriftlich auf die
              Vertraulichkeit zu verpflichten. Dies ist nicht erforderlich, wenn
              die beschäftigten Personen bereits einer angemessenen gesetzlichen
              Verschwiegenheitspflicht unterliegen. Der Auftragnehmer wird die
              in dieser Ziffer niedergelegte Verpflichtung schriftlich
              dokumentieren und sie auf Verlangen des Auftraggebers diesem
              vorlegen.
            </li>
            <li>
              Der Auftragnehmer bestätigt, dass ihm die einschlägigen
              datenschutzrechtlichen Vorschriften bekannt sind. Der
              Auftragnehmer sichert zu, dass er die bei der Durchführung der
              Arbeiten beschäftigten Mitarbeiter mit den für sie maßgebenden
              Bestimmungen des Datenschutzes vertraut macht und er diese auf die
              Einhaltung der geltenden Datenschutzvorschriften zu verpflichten.
              Er überwacht die Einhaltung der datenschutzrechtlichen
              Vorschriften.
            </li>
            <li>
              Diese in dieser Ziffer geregelten Verschwiegenheitspflichten
              bestehen auch nach der Beendigung des Vertragsverhältnisses fort.
            </li>
            <li>
              Darüber hinaus ist der Auftragnehmer neben den jeweils geltenden
              gesetzlichen Bestimmungen (insbesondere § 3 TTDSG, § 203 StGB, §§
              4, 23 GeschGehG sowie ggf. besondere berufsständische
              Verschwiegenheitspflichten) auch verpflichtet, alle Informationen
              und Daten, die ihm im Rahmen der vertraglich vereinbarten
              Leistungen zur Kenntnis gelangen, geheim zu halten und nicht an
              Dritte weiterzugeben (vertrauliche Informationen). Vertrauliche
              Informationen sind insbesondere Geschäfts- und
              Betriebsgeheimnisse, Vertragsschlüsse, technische oder
              kaufmännische Informationen jedweder Art bzw. anderweitige
              Angaben, die als vertraulich bezeichnet oder ihrer Natur nach als
              vertraulich anzusehen sind. Dies gilt insbesondere auch für: br
              Namen, Anschriften sowie die persönlichen, rechtlichen und
              wirtschaftlichen Verhältnisse aller Kunden vom Auftraggeber und
              die persönlichen, rechtlichen und wirtschaftlichen Verhältnisse
              vom Auftraggeber und aller anderen für Auftraggeber tätigen
              Personen. <br />
              Eine Information ist nicht als vertraulich anzusehen, wenn sie zu
              der Zeit, zu der der Auftragnehmer von der Information Kenntnis
              erlangt hat, bereits öffentlich bekannt gewesen ist. Ebenso als
              nicht vertraulich sind solche Informationen anzusehen, die
              zeitlich später mit Zustimmung des Auftraggebers öffentlich
              bekannt geworden sind bzw. bekannt gemacht wurden.
            </li>
            <li>
              Der Auftragnehmer verpflichtet sich, sämtliche Mitarbeiter, die im
              Rahmen der Tätigkeit für den Auftraggeber Kenntnis von
              vorgenannten vertraulichen Informationen erlangen, ebenso wie sich
              selbst zur Vertraulichkeit zu verpflichten.
            </li>
            <li>
              Beauftragt der Auftragnehmer Dritte, hat er dafür Sorge zu tragen,
              dass die Forderungen der Absätze 1 bis 6 entsprechend umgesetzt
              werden.
            </li>
          </DocumentList>
        </DocumentSegment>
        <DocumentSegment title="§ 13 Schlussbestimmungen">
          <DocumentList>
            <li>
              Die Parteien sind sich darüber einig, dass die Einrede des
              Zurückbehaltungsrechts durch den Auftragnehmer i. S. d. § 273 BGB
              hinsichtlich der zu verarbeitenden Daten und der zugehörigen
              Datenträger ausgeschlossen ist.
            </li>
            <li>
              Änderungen und Ergänzungen dieser Vereinbarung bedürfen der
              elektronischen Form.
            </li>
            <li>
              Die Regelungen dieses Vertrags gehen im Zweifel den Regelungen des
              Hauptvertrags vor. Sollten sich einzelne Bestimmungen dieser
              Vereinbarung ganz oder teilweise als unwirksam oder undurchführbar
              erweisen oder infolge Änderungen einer Gesetzgebung nach
              Vertragsabschluss unwirksam oder undurchführbar werden, wird
              dadurch die Wirksamkeit der übrigen Bestimmungen nicht berührt. An
              die Stelle der unwirksamen oder undurchführbaren Bestimmung soll
              die wirksame und durchführbare Bestimmung treten, die dem Sinn und
              Zweck der nichtigen Bestimmung möglichst nahekommt.
            </li>
            <li>
              Diese Vereinbarung unterliegt deutschem Recht. Ausschließlicher
              Gerichtsstand ist der Sitz des Auftragnehmers.
            </li>
          </DocumentList>
        </DocumentSegment>
      </section>
      <section className="mt-6">
        <Title as="h1" className="mb-1" bold>
          Anlagen
        </Title>
        <ol className="mb-8">
          <li>
            <b>Anlage 1</b> &mdash; Festlegungen zum Vertrag
          </li>
          <li>
            <b>Anlage 2</b> &mdash; Technische und organisatorische Maßnahmen
            des Auftragnehmers (Art. 32 DSGVO)
          </li>
        </ol>

        <div className="mb-8">
          <Title as="h3" className="mb-2" bold>
            Anlage 1 &mdash; Festlegungen zum Vertrag
          </Title>
          <table className="table-auto text-left">
            <colgroup>
              <col className="w-1/3" />
              <col className="w-2/3" />
            </colgroup>
            <tbody className="align-text-top">
              <tr>
                <Cell
                  colSpan={2}
                  className="bg-gray-100 border-b border-b-gray-300"
                >
                  <p>
                    <b>Gegenstand und Dauer des Auftrages</b>
                    <br />
                    Übersicht der Anforderungen und Festlegungen
                  </p>
                </Cell>
              </tr>
              <tr>
                <Cell bold>(1) Hauptvertrag</Cell>
                <Cell>
                  Lizenzvertrag zur Inanspruchnahme der “Club of Code”-Software{" "}
                </Cell>
              </tr>
              <tr>
                <Cell bold>(2) Gegenstand des Auftrags</Cell>
                <Cell>
                  Software zur Abbildung von Recruiting-Prozessen von
                  Software-Entwicklern und ähnlichen Berufen in Festanstellung
                  oder einer freiberuflichen Tätigkeit.
                </Cell>
              </tr>
              <tr>
                <Cell bold>
                  (3) Zweck der Datenerhebung, Datenverarbeitung oder
                  Datennutzung
                </Cell>
                <Cell>
                  Zur Erfüllung der Pflichten des Auftragnehmers aus dem
                  Hauptvertrag werden personenbezogene Daten aus dem
                  Herrschaftsbereich des Auftraggebers durch den Auftragnehmer
                  vollumfänglich i. S. d. Art. 4 Nr. 2 DSGVO verarbeitet,
                  insbesondere soweit jeweils erforderlich erhoben, gespeichert,
                  verändert, ausgelesen, abgefragt, verwendet, offengelegt,
                  abgeglichen, verknüpft und gelöscht. Der Zweck der
                  Verarbeitung hängt damit von dem jeweils im Hauptvertrag
                  beschriebenen Auftrag ab.
                </Cell>
              </tr>
              <tr>
                <Cell bold>(4) Art der Daten</Cell>
                <Cell>
                  <p>
                    Die von der Verarbeitung betroffenen Kategorien
                    personenbezogener Daten hängen von der Nutzung der
                    Leistungen des Auftragnehmers durch den Auftraggeber ab. Als
                    Gegenstand der Verarbeitung in Betracht kommende Kategorien
                    von Daten sind möglich:
                  </p>
                  <ul>
                    <li>
                      • Stammdaten (z. B. Namen, Anschriften, Geburtsdaten),
                    </li>
                    <li>
                      • Kontaktdaten (z. B. E-Mail-Adressen, Telefonnummern),
                    </li>
                    <li>
                      • Inhaltsdaten (z. B. Fotografien, Videos, Inhalte von
                      Dokumenten),
                    </li>
                    <li>
                      • Vertragsdaten (z. B. Vertragsgegenstand, Laufzeiten,
                      Kunden),
                    </li>
                    <li>
                      • Zahlungsdaten (z. B. Bankverbindungen,
                      Zahlungsdienstleister),
                    </li>
                    <li>
                      • Nutzungsdaten (z. B. Verlauf Web-Dienste,
                      Zugriffszeiten),
                    </li>
                    <li>
                      • Verbindungsdaten (z. B. Geräte-ID, IP-Adressen,
                      URL-Referrer), und
                    </li>
                    <li>
                      • Standortdaten (z. B. GPS-Daten, IP-Geolokalisierung).
                    </li>
                  </ul>
                </Cell>
              </tr>
              <tr>
                <Cell bold>(5) Kreis der Betroffenen</Cell>
                <Cell>
                  <p>
                    Die von der Verarbeitung betroffenen Kategorien betroffener
                    Personen hängen von der Nutzung der Leistungen des
                    Auftragnehmers durch den Auftraggeber ab. Als Kategorien
                    betroffener Personen kommen dabei in Betracht:
                  </p>
                  <ul>
                    <li>• Beschäftigte</li>
                    <li>• Auszubildende und Praktikanten</li>
                    <li>• Bewerber</li>
                    <li>• ehemalige Arbeitnehmer</li>
                    <li>• freie Mitarbeiter </li>
                    <li>• Gesellschafter, Organe der Gesellschaft</li>
                  </ul>
                </Cell>
              </tr>
            </tbody>
          </table>

          {/* TODO: add Unterauftragnehmer */}
          <table className="table-auto text-left w-full my-4">
            <tbody className="align-text-top">
              <tr>
                <Cell className="bg-gray-100 border-b border-b-gray-300" bold>
                  Nr.
                </Cell>
                <Cell className="bg-gray-100 border-b border-b-gray-300" bold>
                  Name des Unterauftragnehmers Anschrift / Land
                </Cell>
                <Cell className="bg-gray-100 border-b border-b-gray-300" bold>
                  Gegenstand der Leistung
                </Cell>
                <Cell className="bg-gray-100 border-b border-b-gray-300" bold>
                  Verarbeitete personenbezogene Daten
                </Cell>
              </tr>
              <tr>
                <Cell>1</Cell>
                <Cell>
                  <p>
                    Digital Ocean, LLC. (digitalocean.com)
                    <br />
                    101 Avenue of the Americas, 2nd Floor
                    <br />
                    New York, NY 10013
                  </p>
                </Cell>
                <Cell>Hosting-Dienstleistungen</Cell>
                <Cell>Siehe oben „Art der Daten“</Cell>
              </tr>
              <tr>
                <Cell>1</Cell>
                <Cell>
                  <p>
                    ActiveCampaign, LLC (postmarkapp.com)
                    <br />
                    1 North Dearborn St, 5th Floor
                    <br />
                    Chicago, IL 60602
                  </p>
                </Cell>
                <Cell>E-Mail-Versand</Cell>
                <Cell>Siehe oben „Art der Daten“</Cell>
              </tr>
              <tr>
                <Cell>1</Cell>
                <Cell>
                  <p>
                    Stripe Payments Europe, Limited (SPEL) (stripe.com)
                    <br />
                    1 Grand Canal Street Lower
                    <br />
                    Grand Canal Dock
                    <br />
                    Dublin
                    <br />
                    D02 H210
                    <br />
                    Irland
                  </p>
                </Cell>
                <Cell>Bezahldienstleister</Cell>
                <Cell>Siehe oben „Art der Daten“</Cell>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <Title as="h3" className="mb-2" bold>
            Anlage 2 &mdash; Technische und organisatorische Maßnahmen des
            Auftragnehmers (Art. 32 DSGVO)
          </Title>
          <p>
            Verantwortliche für die Datenverarbeitung sind gem. Art. 32 DSGVO
            verpflichtet, technische und organisatorische Maßnahmen zu treffen,
            durch die die Sicherheit der Verarbeitung personenbezogener Daten
            gewährleistet wird. Maßnahmen müssen dabei so gewählt sein, dass
            durch sie in der Summe ein angemessenes Schutzniveau sichergestellt
            wird. Diese Übersicht erläutert vor diesem Hintergrund, welche
            konkreten Maßnahmen durch den Auftragnehmer im Hinblick auf die
            Verarbeitung personenbezogener Daten im konkreten Fall getroffen
            sind.
          </p>
          <div className="space-y-4 mt-2">
            <MeasuresTable
              title="1. Organisation der Informationssicherheit"
              description="Es sind Richtlinien, Prozesse und Verantwortlichkeiten festzulegen, mit denen die Informationssicherheit implementiert und kontrolliert werden kann."
              measures={[
                "Festlegung der Rollen und Verantwortlichkeiten für Betrieb von Anwendungen und System, Datenschutz und Informationssicherheit.",
                "Verpflichtung der Mitarbeiter auf Geheimhaltung und Wahrung des Datengeheimnisses.",
              ]}
            />
            <MeasuresTable
              title="2. Privacy by Design"
              description="Privacy by Design beinhaltet den Gedanken, dass Systeme so konzipiert und konstruiert sein sollten, dass der Umfang der verarbeiteten personenbezogenen Daten minimiert wird. Wesentliche Elemente der Datensparsamkeit sind die Trennung personenbezogener Identifizierungsmerkmale und der Inhaltsdaten, die Verwendung von Pseudonymen und die Anonymisierung. Außerdem muss das Löschen von personenbezogenen Daten gemäß einer konfigurierbaren Aufbewahrungsfrist realisiert werden."
              measures={[
                "Es werden nicht mehr personenbezogene Daten erhoben, als für den jeweiligen Zweck erforderlich sind.",
                "Die Verarbeitungen und Systeme sind so konzipiert, dass Sie ein DSGVO konformes Löschen der verarbeiteten personenbezogenen Daten ermöglichen und sicherstellen.",
              ]}
            />
            <MeasuresTable
              title="3. Privacy by Default"
              description="Privacy by Default bezieht sich auf die datenschutzfreundlichen Voreinstellungen / Standardeinstellungen. Inwieweit wurden diese von Ihnen vorgenommen? Beispiel: Bei einem Besuch einer Webseite kann der Besucher erwarten, dass alle Programme zunächst deaktiviert sind, die personenbezogene Daten erheben."
              measures={[
                "Trackingfunktionen, die den Betroffenen überwachen, sind standardmäßig deaktiviert.",
                "Sämtliche Vorbelegungen von Auswahlmöglichkeiten erfüllen die Anforderungen der DSGVO in Bezug auf datenschutzfreundliche Voreinstellungen (z.B. keine Vorbelegungen von Opt-ins).",
              ]}
            />
            <MeasuresTable
              title="4. Zugriffskontrolle und Zugangskontrolle"
              description="Maßnahmen, die gewährleisten, dass die zur Benutzung der Datenverarbeitungsverfahren Befugten ausschließlich auf die ihrer Zugriffsberechtigung unterliegenden personenbezogenen Daten bzw. schutzbedürftigen Informationen und Daten zugreifen können (Beschreibung von systemimmanenten Sicherungsmechanismen, Verschlüsslungsverfahren entsprechend dem Stand der Technik. Bei Online-Zugriffen ist klarzustellen, welche Seite für die Ausgabe und Verwaltung von Zugriffssicherungscodes verantwortlich ist.). Der Auftragnehmer gewährleistet, dass die zur Benutzung von IT-Infrastruktur berechtigten Nutzer ausschließlich auf Inhalte zugreifen können, für welche sie berechtigt sind, und dass personenbezogene Daten bei der Verarbeitung und nach dem Speichern nicht unbefugt kopiert, verändert oder gelöscht werden können."
              measures={[
                "Zugriff auf Daten ist eingeschränkt und nur für Berechtigte möglich.",
                "Anzahl der Administratoren auf das „Notwendigste“ reduziert.",

                "Regelmäßige Überprüfung der Berechtigungen.",
                "Passwortrichtlinie, Implementierung komplexer Passwörter.",
                "Einsatz starker Authentifizierung mit mindestens 2 Faktoren aus Wissen, Besitz, Eigenschaften (Pin, Token, Smartcard, biometrische Verfahren).",
              ]}
              additionalMeasures={[
                "Einsatz von Passwort-Managern.",
                "Einsatz von Single-Sign-On-Lösungen.",
                "Verwendung randomisierter, komplexer Passwörter.",
              ]}
            />
            <MeasuresTable
              title="5. Kryptographie und / oder Pseudonymisierung"
              description="Einsatz von Verschlüsselungsverfahren für die Sicherstellung des ordnungsgemäßen und wirksamen Schutzes der Vertraulichkeit, Authentizität oder Integrität von personenbezogenen Daten bzw. schutzbedürftigen Informationen. Maßnahmen, die geeignet sind, eine Identifikation des Betroffenen zu erschweren."
              measures={[
                "Verschlüsselung von Zugängen zum Netzwerkzugängen und -verbindungen.",
                "Einsatz von Pseudonymen, Verfahren zur Pseudonymisierung von Daten.",
              ]}
            />
            <MeasuresTable
              title="6. Betriebsverfahren und Zuständigkeiten"
              description="Sicherstellung des ordnungsgemäßen und sicheren Betriebes von Systemen sowie Verfahren zur Verarbeitung von Informationen."
              measures={[
                "Klare Zuordnung von Verantwortlichkeiten für die System- und Anwendungsbetreuung.",
                "Trennung von Entwicklungs-, Test- und Produktivsystemen.",
                "Einsatz von Systemen zur Verwaltungen von Systemen und Geräten (Assetmanagement, Mobile Device Management, Softwareverwaltung und -verteilung).",
              ]}
            />
            <MeasuresTable
              title="7. Datensicherungen"
              description="Maßnahmen, die gewährleisten, dass personenbezogene Daten bzw. schutzbedürftige Informationen und Daten gegen zufällige Zerstörung oder Verlust geschützt sind."
              measures={["Datensicherungskonzept mit regelmäßigen Backups."]}
            />
            <MeasuresTable
              title="8. Schutz vor Malware und Patchmanagement"
              description="Verhinderung einer Ausnutzung technischer Schwachstellen durch den Einsatz von aktueller Virenschutzsoftware und die Implementierung eines Patchmanagements."
              measures={[
                "Regelmäßige Überwachung des Status von Sicherheitsupdates und Systemschwachstellen.",
                "Einsatz von Anti-Malware-Software.",
                "Regelmäßige Einspielen von Sicherheitspatches und Updates.",
              ]}
            />
            <MeasuresTable
              title="9. Netzwerksicherheitsmanagement"
              description="Es muss ein angemessener Schutz für das Netzwerk implementiert werden, so dass die Informationen und die Infrastrukturkomponenten geschützt werden."
              measures={[
                "Einsatz von Netzwerkmanagementsoftware.",
                "Einsatz von Firewallsystemen.",
                "Benutzerauthentifizierung und Verschlüsselung von externen Zugriffen.",
              ]}
            />
            <MeasuresTable
              title="10. Informationsübertragung"
              description="Maßnahmen, die gewährleisten, dass personenbezogene Daten bzw. schutzbedürftige Informationen und Daten bei der elektronischen Übertragung oder während ihres Transportes oder ihrer Speicherung auf Datenträgern nicht unbefugt gelesen, kopiert, verändert oder entfernt werden können, und dass überprüft sowie festgestellt werden kann, an welche Stellen eine Übermittlung personenbezogener Daten bzw. schutzbedürftiger Informationen sowie Daten durch Einrichtungen zur Datenübertragung vorgesehen ist. 
(Beschreibung der verwendeten Einrichtungen und Übermittlungsprotokolle, z.B. Identifizierung und Authentifizierung, Verschlüsselung entsprechend dem Stand der Technik, automatischer Rückruf, u.a.)"
              measures={[
                "Sichere Datenübertragung zwischen Client und Server.",
                "Angemessener Schutz von Emails, die sensible Informationen / Daten beeinhalten.",
              ]}
            />
            <MeasuresTable
              title="11. Lieferantenbeziehungen"
              description="Maßnahmen betreffend die Informationssicherheit zur Verringerung von Risiken im Zusammenhang mit dem Zugriff von Lieferanten auf die Werte des Unternehmens, sollten mit Sublieferanten / Subunternehmern vereinbart und dokumentiert werden."
              measures={[
                "Auswahl des Auftragnehmers unter Sorgfaltsgesichtspunkten (insbesondere hinsichtlich Datensicherheit).",
                "Laufende Überprüfung des Auftragnehmers und seiner Tätigkeiten.",
              ]}
            />
            <MeasuresTable
              title="12. Management von Informationssicherheitsvorfällen"
              description="Es sind konsistente und wirksame Maßnahmen für das Management von Informationssicherheitsvorfällen (Diebstahl, Systemausfall etc.) zu implementieren."
              measures={[
                "Einbindung des Datenschutz- und Informationssicherheitsbeauftragten bei Datenschutzvorfällen.",
              ]}
            />
            <MeasuresTable
              title="13. Informationssicherheitsaspekte des Business Continuity Management / Notfallmanagements"
              description="Die Aufrechterhaltung der Systemverfügbarkeit in schwierigen Situationen, wie Krisen- oder Schadensfälle. 
Ein Notfallmanagement muss dieses sicherstellen. Die Anforderungen bezüglich der Informationssicherheit sollten bei den Planungen zur Betriebskontinuität und Notfallwiederherstellung festgelegt werden."
              measures={["Einsatz redundanter Systeme."]}
            />
            <MeasuresTable
              title="14. Einhaltung gesetzlicher und vertraglicher Anforderungen"
              description="Implementierung von Maßnahmen zur Vermeidung von Verstößen gegen gesetzliche, amtliche oder vertragliche Verpflichtungen sowie gegen jegliche Sicherheitsanforderungen."
              measures={[
                "Geheimhaltungsverpflichtungen mit Mitarbeitern sowie Sublieferanten und Dienstleistern.",
              ]}
            />
          </div>
        </div>
      </section>
    </LegalLayout>
  );
};

export default DataProcessingAgreementPage;
